import Phaser from 'phaser';
import { SPAWNABLE_TIER_INDICES, TIERS, textureKey, getTierScale } from './shapes';
import { createShape, ShapeBody } from './shapeFactory';

export interface SpawnerBounds {
  leftX: number;
  rightX: number;
  spawnY: number;
  /** Y of the dashed dead line; the held shape is lifted so it never crosses it. */
  deadLineY: number;
}

export interface NextShapePreview {
  setTier: (tierIndex: number) => void;
}

/**
 * A queued non-tier deploy. When set, the held image renders `textureKey`
 * instead of the next-up tier, and the next drop calls `onDrop(x, y)`
 * instead of spawning a tier shape. Used by the bomb powerup so the bomb
 * is queued in the dropper rather than auto-dropping.
 */
export interface SpawnerDeploy {
  textureKey: string;
  /** Half-width used to clamp the held image inside the play area. */
  radius: number;
  onDrop: (x: number, y: number) => void;
}

const COOLDOWN_MS = 450;

/**
 * Spawner owns:
 *   - A non-physics Phaser.Image of the held shape at the top (follows pointer X).
 *   - A queue of the current tier and the next tier.
 *
 * On pointer-up: creates a real Matter body at the held shape's position and
 * the held shape is replaced (after a short cooldown) by the next-tier image.
 */
export class Spawner {
  private scene: Phaser.Scene;
  private bounds: SpawnerBounds;
  private heldImage: Phaser.GameObjects.Image | null = null;
  private currentTier: number;
  private nextTier: number;
  private cooldownUntil = 0;
  private indicator: Phaser.GameObjects.Graphics;
  private deploy: SpawnerDeploy | null = null;
  preview?: NextShapePreview;
  onDrop?: (body: ShapeBody) => void;
  /**
   * Optional gate consulted on each pointerup. If it returns false, the
   * pointerup is treated as not-a-drop (e.g. the user was dragging the wheel).
   */
  shouldDrop?: () => boolean;
  enabled = true;

  constructor(scene: Phaser.Scene, bounds: SpawnerBounds) {
    this.scene = scene;
    this.bounds = bounds;
    this.currentTier = pickSpawnTier();
    this.nextTier = pickSpawnTier();

    this.indicator = scene.add.graphics();
    this.indicator.setDepth(50);

    scene.input.on('pointermove', this.onPointerMove, this);
    scene.input.on('pointerdown', this.onPointerMove, this);
    scene.input.on('pointerup', this.onPointerUp, this);

    this.scheduleSpawn(0);
  }

  destroy(): void {
    this.scene.input.off('pointermove', this.onPointerMove, this);
    this.scene.input.off('pointerdown', this.onPointerMove, this);
    this.scene.input.off('pointerup', this.onPointerUp, this);
    this.indicator.destroy();
    if (this.heldImage) this.heldImage.destroy();
  }

  hasQueuedDeploy(): boolean {
    return this.deploy !== null;
  }

  /** Suppress the next drop. Used by HUD buttons whose pointerup would
   *  otherwise bubble to the scene-level drop handler. */
  suppressNextDrop(ms = 220): void {
    this.cooldownUntil = Math.max(this.cooldownUntil, this.scene.time.now + ms);
  }

  setDeploy(deploy: SpawnerDeploy): void {
    this.deploy = deploy;
    // Replace whatever was in the dropper with the deploy visual.
    if (this.heldImage) {
      this.heldImage.destroy();
      this.heldImage = null;
    }
    // Don't drop on the same tap that activated the deploy — the activating
    // button's pointerup also bubbles to the scene-level pointerup handler.
    this.cooldownUntil = this.scene.time.now + 220;
    this.spawnNext();
  }

  private scheduleSpawn(delay: number): void {
    this.scene.time.delayedCall(delay, () => this.spawnNext());
  }

  private spawnNext(): void {
    if (!this.enabled) return;
    const x = this.clampX(this.lastPointerX ?? (this.bounds.leftX + this.bounds.rightX) / 2);
    const key = this.deploy ? this.deploy.textureKey : textureKey(this.currentTier);
    this.heldImage = this.scene.add.image(x, this.bounds.spawnY, key).setDepth(20);
    // Match the scale createShape() will give the real body on drop (1.32 on
    // tablet/desktop) — an unscaled preview visibly grows the moment it drops.
    // Deploy visuals (e.g. the bomb) are authored at display size; leave them.
    if (!this.deploy) this.heldImage.setScale(getTierScale());
    this.heldImage.setY(this.heldY());
    this.drawIndicator(x);
  }

  private lastPointerX: number | undefined;

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.enabled) return;
    this.lastPointerX = pointer.worldX;
    if (this.heldImage) {
      const x = this.clampX(pointer.worldX);
      this.heldImage.setPosition(x, this.heldY());
      this.drawIndicator(x);
    }
  }

  private onPointerUp(_pointer: Phaser.Input.Pointer): void {
    if (!this.enabled) return;
    if (!this.heldImage) return;
    if (this.scene.time.now < this.cooldownUntil) return;
    if (this.shouldDrop && !this.shouldDrop()) return;
    const x = this.heldImage.x;
    // Drop from where the held shape actually hovers (large shapes sit higher
    // so they clear the dead line) — spawning back at spawnY would visibly
    // teleport the shape down on release.
    const y = this.heldImage.y;
    this.heldImage.destroy();
    this.heldImage = null;
    this.indicator.clear();

    if (this.deploy) {
      const d = this.deploy;
      this.deploy = null;
      d.onDrop(x, y);
    } else {
      const tier = this.currentTier;
      const body = createShape(this.scene, tier, x, y);
      body.setDepth(15);
      this.onDrop?.(body);
      this.currentTier = this.nextTier;
      this.nextTier = pickSpawnTier();
      this.preview?.setTier(this.nextTier);
    }

    this.cooldownUntil = this.scene.time.now + COOLDOWN_MS;
    this.scheduleSpawn(COOLDOWN_MS);
  }

  /** Held-shape Y: spawnY normally, lifted just enough that the shape's
   *  bottom edge keeps a small gap above the dead line. Small tiers are
   *  unaffected; pentagon/crescent (and the bomb) ride higher. */
  private heldY(): number {
    if (!this.heldImage) return this.bounds.spawnY;
    const halfH = this.heldImage.displayHeight / 2;
    return Math.min(this.bounds.spawnY, this.bounds.deadLineY - 10 - halfH);
  }

  private clampX(x: number): number {
    const margin = (this.deploy ? this.deploy.radius : TIERS[this.currentTier].radius * getTierScale()) + 2;
    return Phaser.Math.Clamp(x, this.bounds.leftX + margin, this.bounds.rightX - margin);
  }

  private drawIndicator(x: number): void {
    this.indicator.clear();
    this.indicator.lineStyle(2, 0xffffff, 0.25);
    this.indicator.beginPath();
    this.indicator.moveTo(x, this.bounds.spawnY + 20);
    this.indicator.lineTo(x, this.scene.scale.height - 40);
    this.indicator.closePath();
    this.indicator.strokePath();
  }

  getNextTier(): number {
    return this.nextTier;
  }

  getCurrentTier(): number {
    return this.currentTier;
  }

  /** Restore the dropper queue when a run is rebuilt (e.g. a mid-run theme
   *  change restarts the scene). Refreshes the held image + preview if present. */
  setQueue(current: number, next: number): void {
    this.currentTier = current;
    this.nextTier = next;
    this.preview?.setTier(next);
    if (this.heldImage && !this.deploy) {
      this.heldImage.setTexture(textureKey(current));
      this.heldImage.setScale(getTierScale());
      this.heldImage.setY(this.heldY());
    }
  }
}

function pickSpawnTier(): number {
  const arr = SPAWNABLE_TIER_INDICES;
  return arr[Math.floor(Math.random() * arr.length)];
}
